import React, { useCallback, useEffect, useMemo } from 'react';
import { compose, withHandlers, withState } from 'recompose';
import { graphql } from 'react-apollo';
import { X } from 'lucide-react';
import {
  ALL_TAX_INCLUDED_MSG,
  CONFIRM_CHANGE_PLAN,
  INVALID_BRAINTREE_INSTANCE_MSG,
  PAYMENT_PREVIEW,
  PRIVACY_POLICY_MSG,
  PURCHASE_MSG,
  RENEW_SUBSCRIPTION_MSG,
  SUCESSFULLY_PAYMENT_MSG,
  TERMS_AND_CONDITIONS_MSG,
  TOTAL_AMOUNT_MSG,
} from '../../../constants/localization';
import { Dropin, PaymentMethodPayload } from 'braintree-web-drop-in';
import logError from '../../../utils/logError';
import { toast } from 'react-toastify';
import { OptionProps } from '@apollo/react-hoc/lib/types';
import CustomDropIn from './CustomDropIn';
import { messageFromError } from '../../../constants/ui';
import {
  priceCurrencyToString,
  tearDownDropInInstance,
} from '../../../constants/payment';
import {
  MAKE_SUBSCRIPTION,
  UPDATE_PAYMENT_SUBSCRIPTIONS_PLAN,
} from '../../../constants/gql/payment';
import {
  PRIVACY_POLICY_ROUTE,
  TERMS_AND_CONDITIONS_ROUTE,
} from '../../../constants/routes';
import { Link } from 'react-router-dom';
import '../../../redesign/Subsciptions/modals/ConfirmSubscriptionModal.css';
import '../../../redesign/Subsciptions/modals/ChangePaymentMethodModal.css';
import '../../../redesign/Subsciptions/modals/SubscriptionPaymentModal.css';

const Payment: React.FunctionComponent<IProps> = ({
  header,
  price,
  currency,
  makeSubscription,
  changeSubscriptionsPlans,
  isModalOpen,
  setDropInInstance,
  dropInInstance,
  onCloseModal,
  isDisabled,
  errorMessage,
  hasActivePlan,
  isPendingSubscription,
  isRenewedSubscription,
  setDisabled,
}) => {
  const confirmLabel = hasActivePlan
    ? CONFIRM_CHANGE_PLAN
    : isRenewedSubscription
    ? RENEW_SUBSCRIPTION_MSG
    : PURCHASE_MSG;

  const totalAmount = useMemo(
    () => priceCurrencyToString(price, currency),
    [price, currency],
  );

  const isConfirmDisabled = useMemo(
    () => !dropInInstance || isDisabled,
    [dropInInstance, isDisabled],
  );

  const confirmAction = useCallback(() => {
    if (isPendingSubscription) {
      return makeSubscription();
    }
    if (hasActivePlan) {
      return Promise.resolve(changeSubscriptionsPlans());
    }
    return makeSubscription();
  }, [
    changeSubscriptionsPlans,
    hasActivePlan,
    isPendingSubscription,
    makeSubscription,
  ]);

  const handleConfirm = useCallback(async () => {
    if (isConfirmDisabled) {
      return;
    }

    if (hasActivePlan && !isPendingSubscription) {
      setDisabled(true);
    }

    await confirmAction().finally(() => {
      if (hasActivePlan && !isPendingSubscription) {
        setDisabled(false);
      }
    });
  }, [
    confirmAction,
    hasActivePlan,
    isConfirmDisabled,
    isPendingSubscription,
    setDisabled,
  ]);

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onCloseModal();
      }
    },
    [onCloseModal],
  );

  useEffect(() => {
    if (isModalOpen) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    return undefined;
  }, [isModalOpen]);

  if (!isModalOpen) {
    return null;
  }

  return (
    <div className="subscription-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="subscription-modal-container subscription-payment-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="subscription-modal-close"
          onClick={onCloseModal}
          aria-label="Close"
          disabled={isDisabled}
        >
          <X size={20} />
        </button>

        <div className="subscription-modal-content">
          <h2 className="subscription-modal-title">
            {`${PAYMENT_PREVIEW} ${header}`}
          </h2>
          <p className="subscription-modal-description">
            {ALL_TAX_INCLUDED_MSG}
          </p>
        </div>

        <div className="subscription-payment-modal-body">
          <CustomDropIn
            setDropInInstance={setDropInInstance}
            dropInInstance={dropInInstance}
            errorMessage={errorMessage}
          />
        </div>

        <div className="subscription-payment-summary">
          <div className="subscription-payment-summary__row">
            <span>{TOTAL_AMOUNT_MSG}</span>
            <span className="subscription-payment-summary__amount">
              {totalAmount}
            </span>
          </div>
          <div className="subscription-payment-summary__links">
            <Link target="_blank" to={PRIVACY_POLICY_ROUTE}>
              {PRIVACY_POLICY_MSG}
            </Link>
            <Link target="_blank" to={TERMS_AND_CONDITIONS_ROUTE}>
              {TERMS_AND_CONDITIONS_MSG}
            </Link>
          </div>
        </div>

        <div className="subscription-modal-actions subscription-payment-modal-actions">
          <button
            type="button"
            className="subscription-modal-secondary"
            onClick={onCloseModal}
            disabled={isDisabled}
          >
            Cancel
          </button>
          <button
            type="button"
            className="subscription-modal-primary"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {isDisabled ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default compose<IInnerProps, IOuterProps>(
  withState('dropInInstance', 'setDropInInstance', null),
  withState('isDisabled', 'setDisabled', false),
  withState('errorMessage', 'setErrorMessage', null),
  withHandlers({
    onCloseModal: ({
      dropInInstance,
      setDropInInstance,
      toggleModal,
      isDisabled,
    }: IProps) => async () => {
      if (isDisabled) {
        return;
      }
      if (dropInInstance) {
        tearDownDropInInstance(dropInInstance, () => setDropInInstance(null));
      }
      toggleModal(false);
    },
  }),
  graphql(MAKE_SUBSCRIPTION, {
    options: { refetchQueries: ['me'] },
    props: ({ mutate, ownProps }: OptionProps<IProps>) => ({
      makeSubscription: async (): Promise<void> => {
        const {
          dropInInstance,
          successfullyPaymentRedirect,
          setDisabled,
          setErrorMessage,
          planId,
          maxPatients,
          isPendingSubscription,
          isRenewedSubscription,
        } = ownProps;
        setDisabled(true);
        if (!dropInInstance) {
          setErrorMessage(INVALID_BRAINTREE_INSTANCE_MSG);
          setDisabled(false);
          return;
        }
        return dropInInstance
          .requestPaymentMethod()
          .then((paymentMethod: PaymentMethodPayload) =>
            mutate({
              variables: {
                nonce: paymentMethod.nonce,
                planId: planId,
                maxPatients: maxPatients,
                isPendingSubscription: isPendingSubscription,
                isRenewedSubscription: isRenewedSubscription,
              },
            }),
          )
          .then((_) => {
            setErrorMessage(null);
            toast(SUCESSFULLY_PAYMENT_MSG, { type: 'info' });
            successfullyPaymentRedirect();
          })
          .catch((error: Error) => {
            logError('FAILED PAYMENT', error);
            setErrorMessage(messageFromError(error));
            dropInInstance.clearSelectedPaymentMethod();
            setDisabled(false);
          });
      },
    }),
  }),
  graphql(UPDATE_PAYMENT_SUBSCRIPTIONS_PLAN, {
    props: ({ mutate, ownProps }: OptionProps<IProps>) => ({
      changeSubscriptionsPlans: async (): Promise<void> => {
        const {
          dropInInstance,
          setDisabled,
          toggleModal,
          planId,
          maxPatients,
        } = ownProps;

        setDisabled(true);

        return mutate({
          variables: {
            planId,
            maxPatients,
          },
        })
          .then(() => {
            toast(SUCESSFULLY_PAYMENT_MSG, { type: 'info' });
            toggleModal(false);
          })
          .catch((error: Error) => {
            logError('FAILED CHANGE PLAN', error);
            toast(messageFromError(error), { type: 'error' });
            setDisabled(false);
          })
          .finally(() => {
            if (dropInInstance) {
              tearDownDropInInstance(dropInInstance, () => {});
            }
          });
      },
    }),
  }),
)(Payment);

type IProps = IInnerProps & IOuterProps;

interface IInnerProps {
  makeSubscription: () => Promise<void>;
  changeSubscriptionsPlans: () => Promise<void>;
  instance: any;
  setInstance: (value: Dropin) => void;
  dropInInstance?: Dropin;
  setDropInInstance: (value: Dropin) => void;
  onCloseModal: () => void;
  setDisabled: (value: boolean) => void;
  isDisabled: boolean;
  errorMessage: string | null;
  setErrorMessage: (value: string) => void;
}

interface IOuterProps {
  isModalOpen: boolean;
  isPatient: boolean;
  maxPatients: number;
  toggleModal: (value: boolean) => void;
  successfullyPaymentRedirect: () => void;
  hasActivePlan: boolean;
  isPendingSubscription: boolean;
  isRenewedSubscription: boolean;
  header: string;
  price: string;
  currency: string;
  planId: string;
}
